import {
  Circle,
  Loader2,
  CheckCircle2,
  RefreshCw,
  AlertTriangle,
  type LucideIcon,
} from 'lucide-react';
import type { ConnState } from './store.js';

/**
 * Fonte única do mapeamento estado de conexão → forma visual (P13: vocabulário
 * de cor semântico fechado; nenhum token novo). Consumido pelo dot do `ConnChip`
 * (header) e pelo card do `DiscoverySheet`. Tokens validados pelo agente ui-ux
 * em `docs/design/2026-05-30-status-conexao-por-plataforma.md`.
 *
 * `animate` marca os estados transitórios (connecting/reconnecting): o ícone gira
 * e o dot pulsa — indicador essencial de progresso (exceção consciente a P17,
 * congelada sob `prefers-reduced-motion` pela regra global). Estados terminais
 * (connected/failed/idle) são estáticos: "acabou, pode parar de clicar".
 */
export interface ConnVisual {
  /** Rótulo base; consumidores podem sufixar (ex.: countdown no reconnecting). */
  label: string;
  Icon: LucideIcon;
  animate: boolean;
  /** `bg-*` do dot de 8px no chip do header. */
  dotClass: string;
  /** `text-*` do ícone/label. */
  textClass: string;
  /** `border-*` do card no sheet. */
  cardBorder: string;
  /** `bg-*` do card no sheet. */
  cardBg: string;
}

const MAP: Record<ConnState, ConnVisual> = {
  idle: {
    label: 'Não conectado',
    Icon: Circle,
    animate: false,
    dotClass: 'bg-fg-subtle',
    textClass: 'text-fg-muted',
    cardBorder: 'border-border',
    cardBg: 'bg-bg-card',
  },
  connecting: {
    label: 'Conectando…',
    Icon: Loader2,
    animate: true,
    dotClass: 'bg-info',
    textClass: 'text-info',
    cardBorder: 'border-info/40',
    cardBg: 'bg-info-soft',
  },
  connected: {
    label: 'Conectado',
    Icon: CheckCircle2,
    animate: false,
    dotClass: 'bg-success',
    textClass: 'text-success',
    cardBorder: 'border-success/60',
    cardBg: 'bg-success-soft',
  },
  reconnecting: {
    label: 'Reconectando',
    Icon: RefreshCw,
    animate: true,
    dotClass: 'bg-warning',
    textClass: 'text-warning',
    cardBorder: 'border-warning/50',
    cardBg: 'bg-warning-soft',
  },
  failed: {
    label: 'Falha ao conectar',
    Icon: AlertTriangle,
    animate: false,
    dotClass: 'bg-destructive',
    textClass: 'text-destructive',
    cardBorder: 'border-destructive/60',
    cardBg: 'bg-destructive-soft',
  },
  // Terminal não-destrutivo (live acabou / mod soltou). Família muted (como idle),
  // diferenciado pelo ícone preenchido (CheckCircle2, mesmo glifo do connected mas
  // recolorido pra muted → "encerrado, ok", sem celebrar) e pelo dot um degrau
  // acima do idle (`fg-muted` vs `fg-subtle`). Tokens travados pelo ui-ux.
  ended: {
    label: 'Encerrado',
    Icon: CheckCircle2,
    animate: false,
    dotClass: 'bg-fg-muted',
    textClass: 'text-fg-muted',
    cardBorder: 'border-border',
    cardBg: 'bg-bg-card',
  },
};

export function connVisual(state: ConnState): ConnVisual {
  return MAP[state];
}
